import type {
  OpenAiAllowedModels,
  OpenAiImageAllowedModels,
  GeminiImageAllowedModels,
  GeminiTextAllowedModels,
  ClaudeAllowedModels,
  GrokAllowedModels,
  GrokImageAllowedModels,
} from './ai-models.model';
import type { OpenAiReasoningEffort } from './open-ai-reasoning.model';

/**
 * Single source of truth for every place in the app that picks an AI model.
 *
 * Each entry is one *use case* (a feature/pipeline step that calls a model) with
 * the model it defaults to. Use-case sites must read their default from here so
 * there is exactly one place to see and change which model each feature uses —
 * surfaced read-only in the AI Admin "Models" page.
 */
type AiModelUseCaseBase = {
  /** Stable identifier, also the registry key. */
  id: string;
  /** Human label shown in the admin table. */
  label: string;
  /** What this model is used for. */
  description: string;
  /** Env var that overrides this default at runtime, if any. */
  envOverride?: string;
  /**
   * Whether this use case lets the caller (usually an admin) pick a model in
   * the UI per request. The picked value is dynamic and per-request, so the
   * admin page only shows that it is supported — not a fixed value.
   */
  uiSelectable?: boolean;
};

/**
 * Tagged union on the provider and modality so `defaultModel` is constrained
 * to the matching allowlist — a gemini use case cannot default to a GPT chat
 * model, an OpenAI image use case cannot default to a chat model, and a use
 * case that needs a textual answer cannot default to an image model.
 */
export type AiModelUseCase = AiModelUseCaseBase &
  (
    | {
        provider: 'openai';
        modality: 'text';
        defaultModel: OpenAiAllowedModels;
        /**
         * Fixed `reasoning.effort` for requests on this use case's model.
         * Omitted: the provider's default effort for the model applies.
         */
        reasoningEffort?: OpenAiReasoningEffort;
      }
    | {
        provider: 'openai';
        modality: 'image';
        defaultModel: OpenAiImageAllowedModels;
      }
    | { provider: 'claude'; defaultModel: ClaudeAllowedModels }
    | {
        provider: 'grok';
        modality: 'text';
        defaultModel: GrokAllowedModels;
      }
    | {
        provider: 'grok';
        modality: 'image';
        defaultModel: GrokImageAllowedModels;
      }
    | {
        provider: 'gemini';
        modality: 'image';
        defaultModel: GeminiImageAllowedModels;
      }
    | {
        provider: 'gemini';
        modality: 'text';
        defaultModel: GeminiTextAllowedModels;
      }
  );

/** Derived from the union so it stays in sync with the provider arms. */
export type AiModelProvider = AiModelUseCase['provider'];

export const AI_MODEL_USE_CASES = {
  aiChat: {
    id: 'aiChat',
    label: 'AI chat pipeline',
    description:
      'Default model for the AI chat pipeline when a message has no per-message model override.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
    uiSelectable: true,
  },
  openAiService: {
    id: 'openAiService',
    label: 'OpenAI service (generic)',
    description:
      'Fallback model for the generic OpenAI service when a request omits a model.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6-luna',
    // Keeps the cheap fallback fast; Luna would otherwise reason at medium.
    reasoningEffort: 'none',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileClassifier: {
    id: 'projectFileClassifier',
    label: 'Project file classifier',
    description:
      'Classifies uploaded project files into segments and project metadata.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileLabelConsolidation: {
    id: 'projectFileLabelConsolidation',
    label: 'Project file label consolidation',
    description:
      'Consolidates extracted labels across a batch of project files.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileSegmentExtraction: {
    id: 'projectFileSegmentExtraction',
    label: 'Project file segment extraction',
    description: 'Extracts structured data from a project-file segment.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileBlueprintExtraction: {
    id: 'projectFileBlueprintExtraction',
    label: 'Project file blueprint extraction',
    description: 'Extracts joined blueprint data from project files.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileDrawingDetection: {
    id: 'projectFileDrawingDetection',
    label: 'Project file drawing crop',
    description:
      'Finds drawing regions on uploaded sheets so each ritning can be cropped and stored.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  ffuProjectLocation: {
    id: 'ffuProjectLocation',
    label: 'FFU project location',
    description:
      'Looks up the coordinates of the site an FFU names (address, block, municipality) with web search, for the project created from it.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileDrawingFootprint: {
    id: 'projectFileDrawingFootprint',
    label: 'Project file plan footprint',
    description:
      'Traces the building footprint on each cropped plan drawing so storeys get design shapes.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileSiteModel: {
    id: 'projectFileSiteModel',
    label: 'Project file site model',
    description:
      'Reads every plan page of an import at once and returns each building and storey as outlines in metres, placed as they stand on the site.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  projectFileDrawingScale: {
    id: 'projectFileDrawingScale',
    label: 'Project file plan scale check',
    description:
      'Decides which scale a plan sheet is drawn at when its scale bar, printed ratio and the other sheets of the building disagree, from what the drawing shows.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  epdPdfExtraction: {
    id: 'epdPdfExtraction',
    label: 'EPD PDF extraction',
    description: 'Extracts GWP and conversion factors from uploaded EPD PDFs.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
  },
  aiImportMapping: {
    id: 'aiImportMapping',
    label: 'AI import mapping',
    description:
      'Suggests a Boverket or CO2data.fi generic product and optional conversion factor for one imported resource row.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  pdfGeometryImportGemini: {
    id: 'pdfGeometryImportGemini',
    label: 'Plan geometry import (Gemini)',
    description:
      'Imports geometry from a PDF or CAD plan in the design view — when Gemini is selected.',
    provider: 'gemini',
    modality: 'text',
    defaultModel: 'gemini-3.8-flash',
    uiSelectable: true,
  },
  pdfGeometryImportOpenAi: {
    id: 'pdfGeometryImportOpenAi',
    label: 'Plan geometry import (OpenAI)',
    description:
      'Imports geometry from a PDF or CAD plan in the design view — default provider.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6-astra',
    uiSelectable: true,
  },
  pdfGeometryImportClaude: {
    id: 'pdfGeometryImportClaude',
    label: 'Plan geometry import (Claude)',
    description:
      'Imports geometry from a PDF or CAD plan in the design view — when Claude is selected.',
    provider: 'claude',
    defaultModel: 'claude-sonnet-5-5',
    uiSelectable: true,
  },
  pdfGeometryImportGrok: {
    id: 'pdfGeometryImportGrok',
    label: 'Plan geometry import (Grok)',
    description:
      'Imports geometry from a PDF or CAD plan in the design view — when Grok is selected.',
    provider: 'grok',
    modality: 'text',
    defaultModel: 'grok-4.7',
    uiSelectable: true,
  },
  mapCameraCaptureGemini: {
    id: 'mapCameraCaptureGemini',
    label: 'Map camera capture (Gemini)',
    description:
      'Interprets a captured map/camera image in the design view — default provider.',
    provider: 'gemini',
    modality: 'image',
    defaultModel: 'gemini-3.1-flash-image',
    uiSelectable: true,
  },
  mapCameraCaptureOpenAi: {
    id: 'mapCameraCaptureOpenAi',
    label: 'Map camera capture (OpenAI)',
    description:
      'Photorealistic image generation from a captured map/camera view — when OpenAI is selected. The Responses API still uses a mainline chat model; this is the GPT Image tool model.',
    provider: 'openai',
    modality: 'image',
    defaultModel: 'gpt-image-2.5-flare',
    uiSelectable: true,
  },
  mapCameraCaptureGrok: {
    id: 'mapCameraCaptureGrok',
    label: 'Map camera capture (Grok)',
    description:
      'Photorealistic image generation in the design view — when Grok Imagine is selected.',
    provider: 'grok',
    modality: 'image',
    defaultModel: 'grok-imagine-image-2.0',
    uiSelectable: true,
  },
  openAiImageGenerationResponses: {
    id: 'openAiImageGenerationResponses',
    label: 'Generate image (OpenAI Responses)',
    description:
      'Mainline Responses model that calls the image_generation tool for map capture and report covers. The GPT Image model (Flare/Sunburst) is selected separately.',
    provider: 'openai',
    modality: 'text',
    defaultModel: 'gpt-6.1-sol',
    envOverride: 'OPEN_AI_MODEL',
  },
  geminiImageGeneration: {
    id: 'geminiImageGeneration',
    label: 'Gemini image generation',
    description: 'Photorealistic image generation for renderings.',
    provider: 'gemini',
    modality: 'image',
    defaultModel: 'gemini-3.1-flash-image',
    envOverride: 'GEMINI_MODEL',
    uiSelectable: true,
  },
  reportCoverImageGemini: {
    id: 'reportCoverImageGemini',
    label: 'Report cover image (Gemini)',
    description:
      'Photorealistic cover image for the project report. Generation uses the shared Generate-image dialog and `/design-ai/renderings`.',
    provider: 'gemini',
    modality: 'image',
    defaultModel: 'gemini-3.1-flash-image',
    uiSelectable: true,
  },
} as const satisfies Record<string, AiModelUseCase>;

export type AiModelUseCaseId = keyof typeof AI_MODEL_USE_CASES;

/** Iterable view of the registry for the admin table. */
export const AI_MODEL_USE_CASE_LIST: readonly AiModelUseCase[] =
  Object.values(AI_MODEL_USE_CASES);
